import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Target, TrendingUp, DollarSign, Eye, MessageCircle,
  ChevronLeft, ChevronRight, Search, ExternalLink, Phone, Flame, Download,
} from 'lucide-react';
import axios from 'axios';
import { generateBudgetPdf } from '../utils/generatePdf';
import { getPackageById } from '../data/mockData';
import { LogoutButton } from '../components/LogoutButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  lead_capturado: { label: 'Capturado', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  orcamento_gerado: { label: 'Orçamento', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  lead_quente: { label: 'Lead Quente', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  nao_clicou: { label: 'Não Clicou', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  lead_frio: { label: 'Lead Frio', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  em_negociacao: { label: 'Negociação', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  fechado: { label: 'Fechado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  perdido: { label: 'Perdido', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

const ALL_STATUSES = Object.keys(STATUS_LABELS);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [dashRes, leadsRes] = await Promise.all([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/leads${filterStatus ? `?status=${filterStatus}` : ''}`)
      ]);
      setStats(dashRes.data);
      setLeads(leadsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredLeads = leads.filter((l) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return l.name?.toLowerCase().includes(q) || l.whatsapp?.includes(q) || l.event_type?.includes(q);
  });

  const updateStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}/status`, { status: newStatus });
      fetchData();
    } catch (e) { console.error(e); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#8B6F47] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] font-body">
      {/* Header */}
      <header className="bg-[#111111] border-b border-white/5 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-white/40 hover:text-white transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="font-display text-lg text-white">Painel <span className="text-[#C9A961]">Admin</span></h1>
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            <button onClick={() => navigate('/admin/precos')} data-testid="nav-precos" className="font-body text-[11px] sm:text-xs text-[#C9A961] hover:text-[#D4B483] transition-colors uppercase tracking-wider">
              Preços
            </button>
            <button onClick={() => navigate('/admin/calendario')} className="font-body text-[11px] sm:text-xs text-[#C9A961] hover:text-[#D4B483] transition-colors uppercase tracking-wider">
              Calendário
            </button>
            <LogoutButton />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
            {[
              { label: 'Visitantes', value: stats.total_visitors, icon: Eye, color: 'text-[#C9A961]' },
              { label: 'Leads', value: stats.total_leads, icon: Users, color: 'text-[#D4B483]' },
              { label: 'Conversão', value: `${stats.conversion_rate}%`, icon: Target, color: 'text-[#C9A961]' },
              { label: 'Ticket Médio', value: `R$${Math.round(stats.avg_ticket).toLocaleString('pt-BR')}`, icon: DollarSign, color: 'text-[#D4B483]' },
              { label: 'Hot Leads (60+)', value: stats.hot_leads ?? 0, icon: Flame, color: 'text-orange-400' },
              { label: 'Score Médio', value: stats.avg_score ?? 0, icon: TrendingUp, color: 'text-[#C9A961]' },
            ].map((s) => (
              <div key={s.label} className="p-4 rounded-sm border border-white/5 bg-[#111111]">
                <div className="flex items-center gap-2 mb-2">
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                  <span className="font-body text-[10px] uppercase tracking-wider text-white/30">{s.label}</span>
                </div>
                <span className="font-display text-2xl text-white">{s.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Status Chips */}
        {stats?.statuses && Object.keys(stats.statuses).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            <button onClick={() => setFilterStatus('')}
              className={`px-3 py-1.5 rounded-full text-[11px] border transition-all ${
                !filterStatus ? 'border-[#C9A961] bg-[#8B6F47]/15 text-[#D4B483]' : 'border-white/10 text-white/40'
              }`}>Todos ({leads.length})</button>
            {Object.entries(stats.statuses).map(([status, count]) => {
              const info = STATUS_LABELS[status] || { label: status, color: 'bg-white/5 text-white/50 border-white/10' };
              return (
                <button key={status} onClick={() => setFilterStatus(filterStatus === status ? '' : status)}
                  className={`px-3 py-1.5 rounded-full text-[11px] border transition-all ${
                    filterStatus === status ? info.color : 'border-white/10 text-white/40'
                  }`}>{info.label} ({count})</button>
              );
            })}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, WhatsApp ou evento..."
            className="w-full pl-10 pr-4 py-2.5 rounded-sm bg-[#111111] border border-white/10 text-white font-body text-sm focus:border-[#8B6F47]/40 focus:outline-none transition-colors placeholder:text-white/20" />
        </div>

        {/* Leads Table */}
        <div className="border border-white/5 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#111111]">
                  <th className="text-left px-4 py-3 font-body text-[10px] uppercase tracking-wider text-white/30">Nome</th>
                  <th className="text-left px-4 py-3 font-body text-[10px] uppercase tracking-wider text-white/30 hidden sm:table-cell">WhatsApp</th>
                  <th className="text-left px-4 py-3 font-body text-[10px] uppercase tracking-wider text-white/30 hidden md:table-cell">Evento</th>
                  <th className="text-left px-4 py-3 font-body text-[10px] uppercase tracking-wider text-white/30">Valor</th>
                  <th className="text-left px-4 py-3 font-body text-[10px] uppercase tracking-wider text-white/30">Score</th>
                  <th className="text-left px-4 py-3 font-body text-[10px] uppercase tracking-wider text-white/30">Status</th>
                  <th className="text-left px-4 py-3 font-body text-[10px] uppercase tracking-wider text-white/30">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.length === 0 ? (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-white/20 font-body text-sm">Nenhum lead encontrado</td></tr>
                ) : filteredLeads.map((lead) => {
                  const statusInfo = STATUS_LABELS[lead.status] || { label: lead.status, color: 'bg-white/5 text-white/50 border-white/10' };
                  const score = lead.lead_score ?? 0;
                  const scoreColor = score >= 75 ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                                     score >= 50 ? 'bg-[#C9A961]/20 text-[#C9A961] border-[#C9A961]/30' :
                                     score >= 25 ? 'bg-white/[0.05] text-white/55 border-white/10' :
                                                   'bg-white/[0.03] text-white/30 border-white/8';
                  return (
                    <tr key={lead.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3">
                        <button onClick={() => navigate(`/admin/leads/${lead.id}`)} className="font-body text-sm text-white hover:text-[#C9A961] transition-colors text-left">
                          {lead.name}
                        </button>
                        <span className="block font-body text-[10px] text-white/25 sm:hidden">{lead.whatsapp}</span>
                      </td>
                      <td className="px-4 py-3 font-body text-sm text-white/50 hidden sm:table-cell">{lead.whatsapp}</td>
                      <td className="px-4 py-3 font-body text-sm text-white/50 hidden md:table-cell capitalize">{lead.event_type || lead.service_type}</td>
                      <td className="px-4 py-3 font-body text-sm text-[#D4B483] font-medium">R${lead.total_price?.toLocaleString('pt-BR')}</td>
                      <td className="px-4 py-3">
                        <span data-testid={`lead-score-${lead.id}`} className={`inline-flex items-center justify-center min-w-[42px] px-2 py-1 rounded-full border text-[11px] font-semibold ${scoreColor}`}>
                          {score >= 75 && <Flame className="w-3 h-3 mr-1" />}
                          {score}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <select value={lead.status} onChange={(e) => updateStatus(lead.id, e.target.value)}
                          className="bg-transparent border border-white/10 rounded-sm px-2 py-1 font-body text-[11px] text-white/60 focus:outline-none focus:border-[#8B6F47]/40 cursor-pointer">
                          {ALL_STATUSES.map((s) => (
                            <option key={s} value={s} className="bg-[#111111]">{STATUS_LABELS[s]?.label || s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => navigate(`/admin/leads/${lead.id}`)} className="text-white/30 hover:text-[#C9A961] transition-colors" title="Detalhes">
                            <ExternalLink className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const pkg = lead.package_id ? getPackageById(lead.package_id) : null;
                              const excluded = new Set(lead.excluded_items || []);
                              const activeItems = pkg ? pkg.items.filter((it) => !excluded.has(it.id)) : [];
                              generateBudgetPdf({
                                leadName: lead.name,
                                leadWhatsapp: lead.whatsapp,
                                serviceType: lead.service_type,
                                eventType: lead.event_type,
                                guests: lead.guests,
                                eventDate: lead.event_date,
                                eventLocation: lead.location_to_define ? 'A definir' : (lead.event_location || ''),
                                selectedPackage: pkg,
                                activeItems,
                                substitutePratoQuente: !!lead.substitute_prato_quente,
                                selectedDrinks: lead.drinks || [],
                                selectedPartners: lead.partners || [],
                                deliveryQuantities: lead.delivery_items || {},
                                totalPrice: lead.total_price,
                              });
                            }}
                            data-testid={`pdf-lead-${lead.id}`}
                            className="text-white/30 hover:text-[#C9A961] transition-colors"
                            title="Baixar PDF do orçamento"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <a href={`https://wa.me/55${lead.whatsapp?.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-white/30 hover:text-green-400 transition-colors" title="WhatsApp">
                            <Phone className="w-4 h-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
